// In-memory registry of converted user-chart tiles, keyed by chartId.
//
// The pilotapp serves chart tiles straight from the device filesystem
// (`file://{tilesPath}/{z}/{x}/{y}.png`). A browser has no such filesystem, and
// Mapbox GL JS has no `addProtocol` hook (that is a MapLibre feature). So the
// unzipped tiles are turned into blob object URLs here, and a Mapbox
// `transformRequest` (see userChartTransformRequest) rewrites the sentinel tile
// URLs to those blobs. This module is Mapbox-free so the data/infra layer can
// write to it without importing the renderer.

// Sentinel base for the raster `tiles` template. It must be a well-formed http(s)
// URL so Mapbox accepts it; it is never actually fetched — transformRequest
// rewrites it to the in-memory blob URL before any request goes out.
export const USER_CHART_TILE_URL_PREFIX = 'https://userchart.tiles/'

type ChartTiles = Map<string, string> // key `${z}/${x}/${y}` -> blob object URL

const store = new Map<string, ChartTiles>()

const tileKey = (z: number, x: number, y: number): string => `${z}/${x}/${y}`

let transparentTileUrl: string | null = null

// Side length (px) of the transparent fallback tile. Must match the raster
// source tileSize so Mapbox treats it as a normal tile.
const TRANSPARENT_TILE_SIZE = 256

// Lazily builds a fully transparent PNG tile, served for tiles outside the
// chart's coverage so a raster source does not error on the many viewport tiles
// the chart does not cover. It MUST be the full tile size: a 1x1 PNG is rejected
// by the browser's `createImageBitmap` (which Mapbox GL uses to decode raster
// tiles) with "The source image could not be decoded", flooding the console with
// ~20 errors per chart activation (see task 9). Generating it with the browser's
// own PNG encoder (canvas, transparent by default) guarantees a decodable image
// and avoids hardcoding a fragile base64 literal.
function getTransparentTileUrl(): string {
  if (!transparentTileUrl) {
    const canvas = document.createElement('canvas')
    canvas.width = TRANSPARENT_TILE_SIZE
    canvas.height = TRANSPARENT_TILE_SIZE
    const base64 = canvas.toDataURL('image/png').split(',')[1]
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
    transparentTileUrl = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }))
  }
  return transparentTileUrl
}

export function registerUserChartTiles(chartId: string, tiles: Map<string, Uint8Array>): void {
  const previous = store.get(chartId)
  if (previous) previous.forEach((url) => URL.revokeObjectURL(url))

  const urls: ChartTiles = new Map()
  for (const [key, bytes] of tiles) {
    urls.set(key, URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/png' })))
  }
  store.set(chartId, urls)
}

export function clearUserChartTiles(chartId: string): void {
  const tiles = store.get(chartId)
  if (!tiles) return
  tiles.forEach((url) => URL.revokeObjectURL(url))
  store.delete(chartId)
}

// Revokes every registered chart's blob URLs and empties the store. Used when
// clearing all local user-chart data (e.g. on logout).
export function clearAllUserChartTiles(): void {
  store.forEach((tiles) => tiles.forEach((url) => URL.revokeObjectURL(url)))
  store.clear()
}

// Mapbox raster `tiles` template. The chartId is baked in literally; Mapbox only
// substitutes the {z}/{x}/{y} placeholders.
export function userChartTilesUrlTemplate(chartId: string): string {
  return `${USER_CHART_TILE_URL_PREFIX}${chartId}/{z}/{x}/{y}`
}

export function isUserChartTileUrl(url: string): boolean {
  return url.startsWith(USER_CHART_TILE_URL_PREFIX)
}

// True when this chart's tiles are already in the in-memory store (registered by
// a live conversion this session, or rehydrated from the server). Used to decide
// whether a chart needs its processed tiles downloaded before being shown.
export function hasUserChartTiles(chartId: string): boolean {
  return store.has(chartId)
}

// Resolves a sentinel tile URL (`https://userchart.tiles/<chartId>/<z>/<x>/<y>`)
// to the blob object URL for that tile, or a transparent tile if absent.
export function resolveUserChartTileUrl(url: string): string {
  const path = url.slice(USER_CHART_TILE_URL_PREFIX.length)
  const segments = path.split('/')
  const y = segments.pop()
  const x = segments.pop()
  const z = segments.pop()
  const chartId = segments.join('/')

  const resolved = store.get(chartId)?.get(tileKey(Number(z), Number(x), Number(y)))
  return resolved ?? getTransparentTileUrl()
}
